/**
 * Newsletter: preference drawer, landing page form, and newsletter_subscription
 * tracking (spec 4.4).
 *
 * Footer and popup collect the email and hand it to the drawer. Drawer and landing page
 * both post their native customer form over fetch through AjaxCustomerForm, so success
 * and errors show in place instead of reloading the page.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'gz_newsletter_source';
  var FORM_SOURCES = {
    NewsletterLandingForm: 'landing'
  };

  function trackSubscription(source) {
    window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
    window.GZAnalyticsQueue.push(function (GZAnalytics) {
      GZAnalytics.push({ event: 'newsletter_subscription', form_location: source || 'unknown' });
    });
  }

  // Safety net for a native submit slipping through (JS error, captcha fallback): the
  // AJAX path tracks directly and never redirects, so this normally stays unused.
  document.addEventListener('submit', function (e) {
    var source = FORM_SOURCES[e.target.id];
    if (source) sessionStorage.setItem(STORAGE_KEY, source);
  });

  if (window.location.search.indexOf('customer_posted=true') !== -1) {
    var postedSource = sessionStorage.getItem(STORAGE_KEY) || 'unknown';
    sessionStorage.removeItem(STORAGE_KEY);
    trackSubscription(postedSource);
  }

  // Longest a submit may stay pending: an hCaptcha challenge the visitor never
  // completes gives no callback at all, and the button must not stay disabled.
  var SUBMIT_TIMEOUT = 45000;
  var CAPTCHA_BIND_TIMEOUT = 15000;

  /**
   * Posts a Shopify customer form over fetch, hCaptcha included.
   *
   * Shopify answers a customer form with a redirect back to the page, so the outcome is
   * read out of the re-rendered HTML: the caller says which selectors carry the error
   * and the posted marker on its own page.
   */
  class AjaxCustomerForm {
    constructor(form, options) {
      this.form = form;
      this.options = options || {};
      this.submitBtn = form.querySelector('[type="submit"]');
      this.isSubmitting = false;
      this.captchaBound = null;

      // Shopify's hCaptcha script grabs the form's submit method when it binds, then
      // calls it once the challenge is solved and the token is appended to the form.
      // Replacing it here is what keeps the AJAX flow: that call lands on post().
      this.form.submit = () => this.post();
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      if (this.submitBtn) this.submitBtn.disabled = isSubmitting;
      clearTimeout(this.submitTimer);
      if (isSubmitting) {
        this.submitTimer = setTimeout(() => this.setSubmitting(false), SUBMIT_TIMEOUT);
      }
    }

    // window.Shopify.captcha only exists when the shop has spam protection turned on.
    isCaptchaProtected() {
      return !!(window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function');
    }

    // Shopify drops the submit of a protected form that isn't bound yet, and hCaptcha
    // needs a round trip to load, so callers bind ahead of the first submit.
    bindCaptcha() {
      if (this.captchaBound || !this.isCaptchaProtected()) return;
      this.captchaBound = new Promise((resolve) => {
        window.Shopify.captcha.protect(this.form, resolve);
      });
    }

    waitForCaptcha() {
      return Promise.race([
        this.captchaBound || Promise.resolve(),
        new Promise((resolve, reject) => {
          setTimeout(() => reject(new Error('hCaptcha bind timed out')), CAPTCHA_BIND_TIMEOUT);
        })
      ]);
    }

    onSubmit(e) {
      e.preventDefault();
      if (this.isSubmitting) return;

      // Never reached on a click (the browser validates first), but form.submit() from
      // the captcha path would skip validation, so stop it before hCaptcha listens.
      if (!this.form.checkValidity()) {
        e.stopImmediatePropagation();
        this.form.reportValidity();
        return;
      }

      // Bound: hCaptcha's own submit listener takes over from here and calls post().
      if (this.form.hasAttribute('data-hcaptcha-bound')) {
        this.setSubmitting(true);
        return;
      }

      if (!this.isCaptchaProtected()) {
        this.post();
        return;
      }

      this.setSubmitting(true);
      this.bindCaptcha();
      this.waitForCaptcha().then(
        () => this.form.submit(),
        (error) => {
          console.error('Newsletter captcha unavailable:', error);
          this.setSubmitting(false);
          if (this.options.onError) this.options.onError();
        }
      );
    }

    post() {
      this.setSubmitting(true);
      if (this.options.onStart) this.options.onStart();

      fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' }
      })
        .then((response) => response.text().then((html) => this.handleResponse(response, html)))
        .catch((error) => {
          console.error('Newsletter subscription failed:', error);
          if (this.options.onError) this.options.onError();
        })
        .finally(() => this.setSubmitting(false));
    }

    handleResponse(response, html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var errorEl = doc.querySelector(this.options.errorSelector);
      var errorText = errorEl ? errorEl.textContent.replace(/\s+/g, ' ').trim() : '';

      if (errorText) {
        if (this.options.onError) this.options.onError(errorText);
        return;
      }

      var posted =
        doc.querySelector(this.options.postedSelector) ||
        response.url.indexOf('customer_posted=true') !== -1;

      if (!posted) {
        console.error('Newsletter subscription: unexpected response', response.status, response.url);
        if (this.options.onError) this.options.onError();
        return;
      }

      if (this.options.onSuccess) this.options.onSuccess();
    }
  }

  class NewsletterPreferenceDrawer {
    constructor(root) {
      this.root = root;
      this.drawer = root.querySelector('#NewsletterPreferenceDrawer');
      this.form = root.querySelector('#NewsletterPreferenceForm');
      this.emailInput = root.querySelector('#NewsletterPreferenceEmail');
      this.checkIcon = root.querySelector('.js-nl-pref-check');
      this.successEl = root.querySelector('.js-nl-pref-success');
      this.errorEl = root.querySelector('.js-nl-pref-error');
      this.source = 'modal';

      root.querySelectorAll('.js-nl-pref-close').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.emailInput && this.checkIcon) {
        this.emailInput.addEventListener('input', (e) => {
          var valid = e.target.value !== '' && e.target.validity.valid;
          this.checkIcon.classList.toggle('hidden', !valid);
        });
      }

      if (this.form) {
        this.ajax = new AjaxCustomerForm(this.form, {
          errorSelector: '#NewsletterPreferenceModal .js-nl-pref-error',
          postedSelector: '#NewsletterPreferenceModal .js-nl-pref-posted',
          onStart: () => this.resetMessages(),
          onError: (message) => this.showError(message),
          onSuccess: () => {
            this.showSuccess();
            trackSubscription(this.source);
          }
        });
      }
    }

    open(email, source) {
      if (email && this.emailInput) {
        this.emailInput.value = email;
        if (this.checkIcon) this.checkIcon.classList.remove('hidden');
      }
      if (source) this.source = source;
      this.resetMessages();
      this.show();
      if (this.ajax) this.ajax.bindCaptcha();
    }

    show() {
      this.root.classList.remove('opacity-0', 'pointer-events-none');
      this.drawer.classList.remove('translate-x-full');
      this.root.setAttribute('aria-hidden', 'false');
    }

    close() {
      this.drawer.classList.add('translate-x-full');
      this.root.classList.add('opacity-0', 'pointer-events-none');
      this.root.setAttribute('aria-hidden', 'true');
    }

    resetMessages() {
      if (this.successEl) this.successEl.classList.add('hidden');
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }
      if (this.form) this.form.classList.remove('hidden');
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.root.dataset.errorMessage || 'Something went wrong. Please try again.';
      this.errorEl.classList.remove('hidden');
    }

    showSuccess() {
      if (this.successEl) {
        this.successEl.textContent = this.root.dataset.successMessage || 'Thank you for subscribing!';
        this.successEl.classList.remove('hidden');
      }
      if (this.form) this.form.classList.add('hidden');
    }
  }

  /**
   * Landing page (pages/newsletter). The form is visible from the start, so hCaptcha is
   * bound on first interaction rather than on load: it is a heavy third party and most
   * visits never submit.
   */
  class NewsletterLanding {
    constructor(form) {
      this.form = form;
      this.successEl = document.querySelector('.js-nl-landing-success');
      this.errorEl = document.querySelector('.js-nl-landing-error');

      this.ajax = new AjaxCustomerForm(form, {
        errorSelector: '#NewsletterLandingForm .js-nl-landing-server-error',
        postedSelector: '#NewsletterLandingForm .js-nl-landing-posted',
        onStart: () => this.resetMessages(),
        onError: (message) => this.showError(message),
        onSuccess: () => {
          this.showSuccess();
          trackSubscription('landing');
        }
      });

      form.addEventListener('focusin', () => this.ajax.bindCaptcha(), { once: true });
    }

    resetMessages() {
      if (this.successEl) this.successEl.classList.add('hidden');
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.errorEl.dataset.defaultMessage || 'Something went wrong. Please try again.';
      this.errorEl.classList.remove('hidden');
    }

    showSuccess() {
      if (this.successEl) this.successEl.classList.remove('hidden');
      this.form.classList.add('hidden');
    }
  }

  function init() {
    var root = document.getElementById('NewsletterPreferenceModal');
    if (root) {
      var drawer = new NewsletterPreferenceDrawer(root);

      // The drawer is only reachable through these: the footer and popup forms open it.
      window.openNewsletterPreferenceModal = (email, source) => drawer.open(email, source);
      window.closeNewsletterPreferenceModal = () => drawer.close();
    }

    var landingForm = document.getElementById('NewsletterLandingForm');
    if (landingForm) new NewsletterLanding(landingForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
